import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { App } from '@capacitor/app';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

import { MissionClientService } from 'src/app/client/mission-client';
import { Agent } from 'src/app/client/agent-client';
import { AppState } from 'src/app/store/app-state.model';
import * as AgentAction from 'src/app/store/agent/agent.action';

@Injectable({
  providedIn: 'root',
})
export class ProfilePhotoStoreService {
  private static readonly APPROVAL_POLL_INTERVAL_MS = 5000;
  private static readonly APPROVAL_POLL_ATTEMPTS = 12;

  private readonly photoSubject = new BehaviorSubject<string | null>(null);
  public readonly photo$ = this.photoSubject.asObservable();
  private loading = false;
  private approvalPollTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private remainingApprovalPollAttempts = 0;

  constructor(
    private missionClientService: MissionClientService,
    private store: Store<AppState>
  ) {
    // Sempre que o app volta ao primeiro plano, re-busca a foto aprovada —
    // garante refletir aprovações que saíram enquanto estava em background,
    // sem precisar fechar e reabrir o app.
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this.loadApprovedPhoto(true);
      }
    });
  }

  public getPhoto(): string | null {
    return this.photoSubject.value;
  }

  public loadApprovedPhoto(force = false): void {
    if (this.loading) {
      return;
    }

    if (!force && this.photoSubject.value !== null) {
      return;
    }

    this.loading = true;
    this.missionClientService.getCurrentApprovedProfilePhoto().subscribe({
      next: (response) => {
        const photoUrl = response?.imageUrl || null;
        this.photoSubject.next(photoUrl);
        if (photoUrl) {
          this.stopApprovalPolling();
          this.syncApprovedAgentState(photoUrl);
        }
        this.loading = false;
      },
      error: () => {
        // Em erro transitório (rede/token) mantém a foto atual em vez de
        // limpá-la — evita o avatar "piscar" para o padrão nos refreshes forçados.
        this.loading = false;
      },
    });
  }

  public setPhoto(photoUrl: string | null): void {
    this.photoSubject.next(photoUrl);
  }

  /**
   * Limpa o cache da foto no logout. Sem isto, o BehaviorSubject (singleton em
   * memoria) mantinha a foto do usuario anterior, que aparecia no header do
   * proximo login ate a foto nova ser carregada.
   */
  public clear(): void {
    this.stopApprovalPolling();
    this.loading = false;
    this.photoSubject.next(null);
  }

  public startApprovalPolling(): void {
    this.remainingApprovalPollAttempts = ProfilePhotoStoreService.APPROVAL_POLL_ATTEMPTS;
    this.stopApprovalPolling();
    this.scheduleNextApprovalPoll();
  }

  private scheduleNextApprovalPoll(): void {
    if (this.remainingApprovalPollAttempts <= 0) {
      this.stopApprovalPolling();
      return;
    }

    this.approvalPollTimeoutId = setTimeout(() => {
      this.remainingApprovalPollAttempts -= 1;
      this.loadApprovedPhoto(true);
      if (this.remainingApprovalPollAttempts > 0) {
        this.scheduleNextApprovalPoll();
      } else {
        this.stopApprovalPolling();
      }
    }, ProfilePhotoStoreService.APPROVAL_POLL_INTERVAL_MS);
  }

  private stopApprovalPolling(): void {
    if (this.approvalPollTimeoutId !== null) {
      clearTimeout(this.approvalPollTimeoutId);
      this.approvalPollTimeoutId = null;
    }
  }

  private syncApprovedAgentState(photoUrl: string): void {
    this.store.select((state: AppState) => state.agent).pipe(take(1)).subscribe((agent: Agent | null) => {
      if (!agent) {
        return;
      }

      this.store.dispatch(AgentAction.add({
        agent: {
          ...agent,
          profileImageUrl: photoUrl,
          pendingProfileImage: false,
          profileImageStatus: 'APPROVED',
          profileImageRejectionReason: undefined,
        }
      }));
    });
  }
}
